/**
 * Plantillas de los documentos que se imprimen: el ticket (rollo termico de
 * 80 mm) y la factura (A4). Son HTML autocontenido con su propio CSS de
 * impresion; no dependen de nada de la aplicacion.
 *
 * Todo texto que viene de los datos pasa por esc(): nombres de cliente o
 * titulos de libro con "<" o "&" no pueden romper ni inyectar en el documento.
 */

export interface PrintCompany {
    name: string;
    cif: string;
    address: string;
    phone: string;
    email: string;
    /** Delegacion que emite, si la hay ("-DCHO-ECO"). */
    branch?: string;
    fax?: string;
    /** Numero de asociado, bajo los datos del emisor ("718-43"). */
    associateNumber?: string;
    /** Pie registral ("Sociedad Limitada inscrita en el Reg. Merc. de Madrid, tomo..."). */
    registryNote?: string;
}

export interface PrintLine {
    description: string;
    quantity: number;
    unitPrice: number;
    discountPct: number;
    taxRate: number;
    /** Importe de la linea, IVA incluido, con signo (negativo en devoluciones). */
    lineTotal: number;
    /** ISBN y editorial, que van bajo el titulo ("9788425911514/CENTRO DE ESTUDIOS POLITICOS"). */
    isbn?: string;
    publisher?: string;
}

export interface PrintTicket {
    /** "T1-8" */
    number: string;
    type: 'sale' | 'return';
    createdAt: Date;
    paymentMethod: string;
    subtotal: number;
    taxAmount: number;
    total: number;
    isTaxFree: boolean;
    clientName: string;
    clientCif: string;
    /** Numero de la factura vinculada, si la hay. */
    invoiceNumber: string;
    /** Ticket que rectifica esta devolucion, si es una. */
    originalNumber: string;
    lines: PrintLine[];
    company: PrintCompany;
}

/** Datos del envio. Hoy no los produce nadie: salen las etiquetas en blanco. */
export interface PrintShipping {
    /** Transportista y servicio ("MRW / INTE"). */
    method?: string;
    date?: Date | null;
    packages?: number | string;
    netWeight?: number | string;
    grossWeight?: number | string;
    /** Partida arancelaria ("4901.99.00.00"). */
    tariffCode?: string;
}

/** Cuenta a la que pagar, al pie de la factura. */
export interface PrintBank {
    name?: string;
    iban?: string;
    swift?: string;
}

export interface PrintInvoice {
    number: string;
    issueDate: Date;
    status: string;
    paymentMethod: string;
    subtotal: number;
    taxAmount: number;
    total: number;
    isTaxFree: boolean;
    clientName: string;
    clientCif: string;
    clientEmail: string;
    clientAddress: string;
    /** Ticket del que sale la factura ("T1-8"), si lo hay. */
    ticketNumber: string;
    lines: PrintLine[];
    company: PrintCompany;

    // ── Campos del formato de Marcial Pons. Todos opcionales: mientras no haya
    // de donde sacarlos, la factura imprime la etiqueta con el valor vacio.
    /** Codigo del cliente ("12111963"). */
    clientCode?: string;
    clientPhone?: string;
    /** Poblacion, con su CP si lo hay ("16050 CIUDAD DE MEXICO"). */
    clientCityLine?: string;
    clientCountry?: string;
    /** "REF. CLIENTE 700001130". */
    clientRef?: string;
    /** "Via de pago: Y". Si no viene, se usa el metodo de pago traducido. */
    paymentVia?: string;
    /** "N/REF: 137025". */
    ourRef?: string;
    shippingCost?: number;
    handlingCost?: number;
    shipping?: PrintShipping;
    bank?: PrintBank;
    /** Divisa del documento. EUR si no se dice otra cosa. */
    currency?: string;
    /** Total y valor estadistico en divisa extranjera, en exportacion. */
    foreignCurrency?: string;
    foreignTotal?: number;
    foreignStatisticalValue?: number;
    /** Valor estadistico, para exportacion. */
    statisticalValue?: number;
    page?: number;
    pageCount?: number;
}

const PAYMENT_LABELS: Record<string, string> = {
    cash: 'Efectivo',
    card: 'Tarjeta',
    marcial_card: 'Tarjeta Marcial Pons',
    gift_card: 'Tarjeta o Cheque Regalo',
    invoice: 'Contra factura'
};

export const paymentLabel = (method: string): string => PAYMENT_LABELS[method] || method || '';

export const esc = (value: any): string =>
    String(value === undefined || value === null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

const money = (n: number): string =>
    new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const dateTime = (d: Date): string =>
    d.toLocaleString('es-ES', { timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const dateOnly = (d: Date): string =>
    d.toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit', year: 'numeric' });

/** Importe sin simbolo: en la factura la divisa va aparte. */
const amount = (n: number): string =>
    new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

/** Fecha con puntos ("19.10.2026"), que es como sale en la factura. */
const dateDots = (d: Date): string => dateOnly(d).replace(/\//g, '.');

const round2 = (n: number): number => Math.round(n * 100) / 100;

export interface TaxGroup {
    rate: number;
    base: number;
    tax: number;
}

/**
 * Desglose de IVA por tipo. Los precios del TPV llevan IVA incluido: base =
 * importe / (1 + tipo). Las diferencias de redondeo con el total del documento
 * (centimos) se absorben en el primer grupo, para que base + IVA sumen siempre
 * exactamente lo cobrado.
 */
export const taxBreakdown = (lines: PrintLine[], total: number): TaxGroup[] => {
    const byRate: Record<string, number> = {};
    lines.forEach(l => { byRate[String(l.taxRate)] = (byRate[String(l.taxRate)] || 0) + l.lineTotal; });

    const groups: TaxGroup[] = Object.keys(byRate).map(key => {
        const rate = Number(key);
        const gross = byRate[key];
        const base = round2(gross / (1 + rate / 100));
        return { rate, base, tax: round2(gross - base) };
    });

    if (groups.length > 0) {
        const diff = round2(total - groups.reduce((sum, g) => sum + g.base + g.tax, 0));
        if (Math.abs(diff) > 0 && Math.abs(diff) < 0.1) groups[0].tax = round2(groups[0].tax + diff);
    }
    return groups;
};

const companyBlock = (c: PrintCompany): string =>
    [c.name, c.cif ? `CIF ${c.cif}` : '', c.address, c.phone ? `Tel. ${c.phone}` : '', c.email]
        .filter(Boolean).map(esc).join('<br>');

/**
 * Como esc(), pero respetando los saltos de linea del dato. Las direcciones se
 * guardan en un solo campo de texto con saltos dentro ("calle\nCP ciudad"), y
 * sin esto salen aplastadas en una linea.
 */
const escLines = (value: any): string => esc(value).replace(/\r?\n/g, '<br>');

/** Bloque del emisor para la factura: lleva mas datos que el del ticket. */
const invoiceCompanyBlock = (c: PrintCompany): string =>
    [
        [c.name, c.branch].filter(Boolean).join(' '),
        c.address,
        c.cif ? `CIF/VAT - ${c.cif}` : '',
        c.phone ? `Tel: ${c.phone}` : '',
        c.fax ? `Fax: ${c.fax}` : '',
        c.email,
        c.associateNumber ? `Nº Asociado: ${c.associateNumber}` : ''
    ].filter(Boolean).map(escLines).join('<br>');

// ── Ticket (rollo de 80 mm) ───────────────────────────────────────────────

export const renderTicketHtml = (t: PrintTicket, widthMm = 80): string => {
    const isReturn = t.type === 'return';
    const groups = t.isTaxFree ? [] : taxBreakdown(t.lines, t.total);

    const rows = t.lines.map(l => {
        const hasDiscount = l.discountPct > 0;
        return `<tr>
  <td class="qty">${esc(l.quantity)}</td>
  <td class="desc">${esc(l.description)}${hasDiscount ? `<span class="muted"> (-${esc(l.discountPct)}%)</span>` : ''}<div class="muted">${money(l.unitPrice)} / u</div></td>
  <td class="amt">${money(l.lineTotal)}</td>
</tr>`;
    }).join('');

    const taxRows = groups.map(g => `<tr><td>Base ${esc(g.rate)}%</td><td class="amt">${money(g.base)}</td></tr>
<tr><td>IVA ${esc(g.rate)}%</td><td class="amt">${money(g.tax)}</td></tr>`).join('');

    return `<!doctype html>
<html lang="es"><head><meta charset="utf-8"><title>Ticket ${esc(t.number)}</title>
<style>
  /* El tamaño de papel (80 mm x el alto del contenido) lo fija printHtml al imprimir. */
  @page { margin: 3mm; }
  * { box-sizing: border-box; }
  body { margin: 0; width: ${widthMm - 6}mm; font-family: "Courier New", ui-monospace, monospace; font-size: 11px; line-height: 1.35; color: #000; }
  .center { text-align: center; }
  .title { font-size: 14px; font-weight: 700; margin: 0 0 2px; }
  .muted { color: #444; font-size: 10px; }
  .sep { border-top: 1px dashed #000; margin: 6px 0; }
  .doc { font-size: 13px; font-weight: 700; text-align: center; margin: 4px 0 2px; }
  table { width: 100%; border-collapse: collapse; }
  td { vertical-align: top; padding: 1px 0; }
  td.qty { width: 8mm; }
  td.amt { text-align: right; white-space: nowrap; padding-left: 4px; }
  .total td { font-size: 14px; font-weight: 700; padding-top: 4px; }
</style></head><body>
<div class="center">
  <p class="title">${esc(t.company.name)}</p>
  <div class="muted">${companyBlock({ ...t.company, name: '' })}</div>
</div>
<div class="sep"></div>
<div class="doc">${isReturn ? 'DEVOLUCIÓN' : 'TICKET'} ${esc(t.number)}</div>
<div class="center muted">${esc(dateTime(t.createdAt))}</div>
${isReturn && t.originalNumber ? `<div class="center muted">Rectifica el ticket ${esc(t.originalNumber)}</div>` : ''}
${t.clientName ? `<div class="center muted">Cliente: ${esc(t.clientName)}${t.clientCif ? ` (${esc(t.clientCif)})` : ''}</div>` : ''}
<div class="sep"></div>
<table>${rows}</table>
<div class="sep"></div>
<table>
  ${taxRows}
  <tr class="total"><td>TOTAL</td><td class="amt">${money(t.total)}</td></tr>
</table>
<div class="sep"></div>
<div>Pago: ${esc(paymentLabel(t.paymentMethod))}</div>
${t.isTaxFree ? '<div>Venta sin IVA (tax free)</div>' : ''}
${t.invoiceNumber ? `<div>Factura: ${esc(t.invoiceNumber)}</div>` : ''}
<div class="sep"></div>
<div class="center muted">Gracias por su compra</div>
</body></html>`;
};

// ── Factura (A4) ──────────────────────────────────────────────────────────

/**
 * Mismos campos que la factura que emite Marcial Pons: emisor con delegacion y
 * nº de asociado, caja de cliente, referencias, lineas con PVP con y sin IVA,
 * subtotal y descuento, gastos de envio y gestion, desglose de IVA, bloque de
 * envio con datos aduaneros y pie con la cuenta bancaria y el dato registral.
 *
 * Lo que hoy no produce nadie (envio, aduana, banco, codigo de cliente) sale
 * como etiqueta con el valor vacio, igual que en un impreso.
 */
export const renderInvoiceHtml = (inv: PrintInvoice, layout: 'print' | 'email' = 'print'): string => {
    // En un correo no se puede contar con flex (Outlook no lo entiende, Gmail a
    // medias): las mismas piezas se colocan con tablas, que es lo que respetan
    // todos los clientes. El diseño es el mismo; solo cambia como se alinean.
    const email = layout === 'email';
    const tbl = 'width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation"';
    /** Abre/cierra un bloque de dos columnas: flex en papel, tabla en correo. */
    const cols = {
        open: (cls: string, style = '') => email ? `<table ${tbl}${style ? ` style="${style}"` : ''}><tr><td valign="top">` : `<div class="${cls}">`,
        next: (attrs = '') => email ? `</td><td valign="top" ${attrs}>` : '',
        close: () => email ? '</td></tr></table>' : '</div>',
        // Bloque pegado al borde derecho pero con el texto alineado a la izquierda,
        // como en el papel (con align="right" a secas el texto saldria a la derecha).
        rightOpen: email ? `<table cellpadding="0" cellspacing="0" border="0" role="presentation" align="right"><tr><td align="left">` : '',
        rightClose: email ? '</td></tr></table>' : ''
    };
    const isCredit = inv.total < 0;
    const currency = inv.currency || 'EUR';
    const groups = inv.isTaxFree ? [] : taxBreakdown(inv.lines, inv.total);
    const ship = inv.shipping || {};
    const bank = inv.bank || {};

    const shippingCost = inv.shippingCost || 0;
    const handlingCost = inv.handlingCost || 0;

    // PVP sin IVA de la linea, antes y despues del descuento. El neto es lo que
    // acaba sumando la base imponible.
    // El bruto se calcula del PVP, que siempre es positivo, pero en una
    // rectificativa la linea va en negativo: sin arrastrar el signo, SUBTOTAL y
    // DESCUENTO salen disparatados (96,15 y 158,65 en vez de -96,15 y -33,65).
    const grossOf = (l: PrintLine): number =>
        (l.lineTotal < 0 ? -1 : 1) * (l.unitPrice * l.quantity) / (1 + l.taxRate / 100);

    const subtotal = round2(inv.lines.reduce((sum, l) => sum + grossOf(l), 0));
    const goodsBase = inv.isTaxFree ? inv.total : groups.reduce((sum, g) => sum + g.base, 0);
    // El descuento se saca de la base que se imprime, no de la suma de netos
    // por linea: con dos redondeos independientes el documento se descuadraba
    // un centimo (211,53 - 21,16 daba 190,37 con una base de 190,38). Asi
    // SUBTOTAL - DESCUENTO es siempre exactamente la base imponible.
    const discount = round2(subtotal - goodsBase);
    const taxTotal = groups.reduce((sum, g) => sum + g.tax, 0);
    // Gastos de envio y gestion: hoy entran en la base sin IVA propio. Cuando se
    // facturen de verdad habra que decidir a que tipo tributan.
    const taxBase = round2(goodsBase + shippingCost + handlingCost);
    const grandTotal = round2(taxBase + taxTotal);

    const rows = inv.lines.map(l => {
        const ref = [l.isbn, l.publisher].filter(Boolean).map(esc).join('/');
        return `<tr>
  <td class="qty">${esc(l.quantity)}</td>
  <td>${esc(l.description)}${ref ? `<div class="ref">${ref}</div>` : ''}</td>
  <td class="num">${amount(l.unitPrice)}</td>
  <td class="num">${amount(l.unitPrice / (1 + l.taxRate / 100))}</td>
  <td class="num">${amount(grossOf(l))}</td>
</tr>`;
    }).join('');

    // El bloque de envio y aduana es solo de las facturas que se mandan fuera:
    // en una venta de mostrador no existe y no debe salir ni como etiqueta
    // vacia. Lo mismo con la columna de gastos, que se desdobla en envio y
    // gestion unicamente cuando hay algo que desdoblar.
    const hasShipping = !!(ship.method || ship.date || ship.packages !== undefined ||
        ship.netWeight !== undefined || ship.grossWeight !== undefined || ship.tariffCode || shippingCost > 0);
    const hasForeign = !!(inv.foreignCurrency || inv.foreignTotal !== undefined ||
        inv.statisticalValue !== undefined || inv.foreignStatisticalValue !== undefined);
    const splitCosts = hasShipping || handlingCost > 0;

    // Una fila por tipo de IVA; los gastos y la base solo salen en la primera.
    const rateRows: TaxGroup[] = inv.isTaxFree ? [{ rate: 0, base: goodsBase, tax: 0 }] : groups;
    const costCells = (first: boolean): string => splitCosts
        ? `<td class="num">${first ? amount(shippingCost) : ''}</td><td class="num">${first ? amount(handlingCost) : ''}</td>`
        : `<td class="num">${first ? amount(shippingCost + handlingCost) : ''}</td>`;
    const totalsRows = (rateRows.length > 0 ? rateRows : [{ rate: 0, base: 0, tax: 0 }]).map((g, i) => `<tr>
  ${costCells(i === 0)}
  <td class="num">${i === 0 ? amount(taxBase) : ''}</td>
  <td class="num">${esc(g.rate)}%</td>
  <td class="num">${amount(g.tax)}</td>
</tr>`).join('');

    const field = (label: string, value: string): string =>
        `<div><span class="lbl">${esc(label)}:</span> ${esc(value)}</div>`;

    const docLabel = isCredit ? 'Factura rectificativa' : 'Factura';
    const via = inv.paymentVia || paymentLabel(inv.paymentMethod);

    return `<!doctype html>
<html lang="es"><head><meta charset="utf-8"><title>${esc(docLabel)} ${esc(inv.number)}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: "Helvetica Neue", Arial, sans-serif; font-size: 11px; color: #111; line-height: 1.45; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; }
  .company strong { font-size: 16px; }
  /* Columna derecha de la cabecera: la caja de factura y, debajo, la del
     cliente, las dos AL LADO del emisor y no debajo. */
  .headcol { display: flex; flex-direction: column; gap: 12px; width: 82mm; flex: none; }
  .docbox { border: 1px solid #111; padding: 8px 12px; }
  .docbox div { white-space: nowrap; }
  .clientbox { border: 1px solid #111; padding: 8px 12px; }
  .refs { display: flex; justify-content: space-between; gap: 20px; margin: 14px 0 6px; }
  .lbl { color: #444; }
  /* Cuadricula como la de la tabla de totales: las rayas verticales bajan por
     toda la tabla y el conjunto va enmarcado. Entre linea y linea NO hay raya
     horizontal, igual que en la factura de Marcial Pons. */
  table.lines { width: 100%; border-collapse: collapse; margin-top: 4px; border: 1px solid #111; }
  table.lines th { text-align: left; font-weight: 600; border-bottom: 1px solid #111; border-right: 1px solid #111; padding: 5px 6px; }
  table.lines td { padding: 5px 6px; vertical-align: top; border-right: 1px solid #111; }
  table.lines th:last-child, table.lines td:last-child { border-right: none; }
  table.lines tr.sub td { padding-top: 10px; font-weight: 700; }
  .ref { color: #555; }
  .num { text-align: right; white-space: nowrap; }
  th.num { text-align: right; }
  td.qty, th.qty { width: 14mm; text-align: left; }
  .totals { display: flex; gap: 10px; align-items: flex-start; margin-top: 18px; }
  table.tot { flex: 1; border-collapse: collapse; }
  table.tot th, table.tot td { border: 1px solid #111; padding: 5px 6px; }
  table.tot th { font-weight: 600; text-align: center; }
  .grand { border: 1px solid #111; min-width: 48mm; }
  .grand .ghead { border-bottom: 1px solid #111; padding: 5px 6px; text-align: center; font-weight: 600; }
  .grand .gval { padding: 8px 6px; text-align: right; font-size: 14px; font-weight: 700; }
  .ship { display: flex; justify-content: space-between; gap: 20px; margin-top: 18px; }
  .page { text-align: right; margin-top: 10px; }
  .bank { border: 1px solid #111; padding: 8px 12px; margin-top: 14px; font-weight: 700; }
  .foot { margin-top: 10px; font-size: 9px; color: #555; }
</style></head><body>
${cols.open('head')}
  <div class="company"><strong>${esc(inv.company.name)}</strong><br>${invoiceCompanyBlock({ ...inv.company, name: '' })}</div>
  ${cols.next('width="310" style="width:310px;padding-left:20px"')}<div${email ? '' : ' class="headcol"'}>
    <div class="docbox">
      <div><strong>${esc(docLabel)}: ${esc(inv.number)}</strong></div>
      <div>Fecha: ${esc(dateDots(inv.issueDate))}</div>
    </div>
    ${email ? '<div style="height:12px;line-height:12px">&nbsp;</div>' : ''}<div class="clientbox">
  <div>${esc(inv.clientName || '—')}</div>
  ${inv.clientCode ? `<div>${esc(inv.clientCode)}</div>` : ''}
  ${inv.clientPhone ? `<div>${esc(inv.clientPhone)}</div>` : ''}
  ${inv.clientEmail ? `<div>${esc(inv.clientEmail)}</div>` : ''}
  ${inv.clientAddress ? `<div>${escLines(inv.clientAddress)}</div>` : ''}
  ${inv.clientCityLine ? `<div>${esc(inv.clientCityLine)}</div>` : ''}
  ${inv.clientCountry ? `<div>${esc(inv.clientCountry)}</div>` : ''}
  ${inv.clientCif ? `<div>${esc(inv.clientCif)}</div>` : ''}
    </div>
  </div>
${cols.close()}
${cols.open('refs', 'margin:14px 0 6px')}
  <div>
    ${field('REF. CLIENTE', inv.clientRef || '')}
    ${via ? field('Vía de pago', via) : ''}
  </div>
  ${cols.next('align="right"')}${cols.rightOpen}<div>${field('N/REF', inv.ourRef || '')}</div>${cols.rightClose}
${cols.close()}
<table class="lines">
  <thead><tr>
    <th class="qty">Cant.</th><th>Concepto</th>
    <th class="num">PVP con IVA</th><th class="num">PVP sin IVA</th><th class="num">Total sin IVA</th>
  </tr></thead>
  <tbody>
    ${rows}
    <tr class="sub"><td></td><td>SUBTOTAL</td><td></td><td></td><td class="num">${amount(subtotal)}</td></tr>
    <tr class="sub"><td></td><td>DESCUENTO</td><td></td><td></td><td class="num">${amount(discount)}</td></tr>
  </tbody>
</table>
${cols.open('totals', 'margin-top:18px')}
  <table class="tot"${email ? ' style="width:100%"' : ''}>
    <thead><tr>
      ${splitCosts ? '<th>Gastos envío</th><th>Gastos gestión</th>' : '<th>Gastos</th>'}<th>Base imponible</th><th>Tipos IVA</th><th>Importe IVA</th>
    </tr></thead>
    <tbody>${totalsRows}</tbody>
  </table>
  ${cols.next('width="190" style="width:190px;padding-left:10px"')}<div class="grand">
    <div class="ghead">TOTAL FACTURA</div>
    <div class="gval">${amount(grandTotal)} ${esc(currency)}</div>
  </div>
${cols.close()}
${hasShipping || hasForeign ? `${cols.open('ship', 'margin-top:18px')}
  ${hasShipping ? `<div>
    ${field('Forma envío', ship.method || '')}
    ${field('Fecha de envío', ship.date ? dateDots(new Date(ship.date)) : '')}
    ${field('Nº bultos', ship.packages === undefined || ship.packages === null ? '' : String(ship.packages))}
    ${field('Peso neto', ship.netWeight === undefined || ship.netWeight === null ? 'KG' : `${ship.netWeight} KG`)}
    ${field('Peso bruto', ship.grossWeight === undefined || ship.grossWeight === null ? 'KG' : `${ship.grossWeight} KG`)}
    ${field('Partida Arancelaria', ship.tariffCode || '')}
  </div>` : '<div></div>'}
  ${cols.next('align="right"')}${hasForeign ? `${cols.rightOpen}<div>
    ${field('Total Factura', `${amount(grandTotal)} ${currency}`)}
    ${field('Total Divisa', inv.foreignTotal === undefined ? '' : `${amount(inv.foreignTotal)} ${inv.foreignCurrency || ''}`)}
    ${field('Valor estadístico', inv.statisticalValue === undefined ? '' : `${amount(inv.statisticalValue)} ${currency}`)}
    ${field('Valor estadístico divisa', inv.foreignStatisticalValue === undefined ? '' : `${amount(inv.foreignStatisticalValue)} ${inv.foreignCurrency || ''}`)}
  </div>${cols.rightClose}` : ''}
${cols.close()}` : ''}
<div class="page">Página ${esc(inv.page || 1)} de ${esc(inv.pageCount || 1)}</div>
${bank.name || bank.iban || bank.swift ? `<div class="bank">TRANSFERENCIA: ${esc(bank.name || '')}${bank.iban ? `, IBAN ${esc(bank.iban)}` : ''}${bank.swift ? `<br>SWIFT ${esc(bank.swift)}` : ''}</div>` : ''}
${inv.company.registryNote ? `<div class="foot">${esc(inv.company.registryNote)}</div>` : ''}
</body></html>`;
};

// ── Orden de pedido de cliente (A4) ───────────────────────────────────────

export interface PrintOrderLine {
    title: string;
    quantity: number;
    /** Lo que llevan recibido de esa cantidad. */
    received: number;
    /** Precio de venta, del ticket de origen. 0 si no se pudo averiguar. */
    unitPrice: number;
    lineTotal: number;
}

export interface PrintOrder {
    /** Numero de la orden de compra: "OC-2026-000013". */
    number: string;
    createdAt: Date;
    /** Etiqueta del estado, ya traducida ("Recibido", "Pte. Aprobar"...). */
    statusLabel: string;
    clientName: string;
    supplierName: string;
    expectedDelivery: string;
    /** Ticket en el que el cliente pago el pedido, si se conoce. */
    ticketNumber: string;
    /** false cuando no se pudo leer el ticket: entonces no se enseñan importes. */
    hasPrices: boolean;
    total: number;
    lines: PrintOrderLine[];
    company: PrintCompany;
}

/**
 * Resguardo del pedido para el cliente. Lleva solo SUS lineas, no la orden
 * entera: una orden de compra va a un proveedor y puede juntar libros de varios
 * clientes.
 *
 * Formato A4 propio. Ya no sigue a renderInvoiceHtml, que ahora imprime el
 * formato de factura de Marcial Pons (con datos de envio, aduana y banco) que
 * aqui no pinta nada.
 */
export const renderOrderHtml = (o: PrintOrder): string => {
    const rows = o.lines.map(l => `<tr>
  <td>${esc(l.title)}</td>
  <td class="num">${esc(l.quantity)}</td>
  <td class="num">${l.received >= l.quantity ? 'En tienda' : `${esc(l.received)} de ${esc(l.quantity)}`}</td>
  ${o.hasPrices ? `<td class="num">${money(l.unitPrice)}</td><td class="num">${money(l.lineTotal)}</td>` : ''}
</tr>`).join('');

    return `<!doctype html>
<html lang="es"><head><meta charset="utf-8"><title>Pedido ${esc(o.number)}</title>
<style>
  @page { size: A4; margin: 15mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: "Helvetica Neue", Arial, sans-serif; font-size: 12px; color: #1f2937; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
  .company { font-size: 12px; line-height: 1.5; }
  .company strong { font-size: 18px; color: #3d405b; }
  .doc { text-align: right; }
  .doc h1 { margin: 0 0 6px; font-size: 22px; color: #3d405b; letter-spacing: .04em; text-transform: uppercase; }
  .doc div { line-height: 1.6; }
  .parties { display: flex; gap: 24px; margin: 28px 0 20px; }
  .box { flex: 1; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 14px; line-height: 1.6; }
  .box h2 { margin: 0 0 6px; font-size: 10px; letter-spacing: .12em; text-transform: uppercase; color: #9ca3af; }
  table.lines { width: 100%; border-collapse: collapse; margin-top: 8px; }
  table.lines th { text-align: left; font-size: 10px; letter-spacing: .08em; text-transform: uppercase; color: #6b7280; border-bottom: 2px solid #3d405b; padding: 8px 6px; }
  table.lines td { padding: 8px 6px; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
  .num { text-align: right; white-space: nowrap; }
  th.num { text-align: right; }
  .totals { margin: 18px 0 0 auto; width: 300px; }
  .totals table { width: 100%; border-collapse: collapse; }
  .totals tr.grand td { border-top: 2px solid #3d405b; font-size: 16px; font-weight: 700; color: #3d405b; padding: 10px 6px 5px; }
  .foot { margin-top: 28px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #6b7280; line-height: 1.6; }
</style></head><body>
<div class="head">
  <div class="company"><strong>${esc(o.company.name)}</strong><br>${companyBlock({ ...o.company, name: '' })}</div>
  <div class="doc">
    <h1>Pedido</h1>
    <div><strong>Nº ${esc(o.number)}</strong></div>
    <div>Fecha: ${esc(dateOnly(o.createdAt))}</div>
    ${o.ticketNumber ? `<div>Ticket: ${esc(o.ticketNumber)}</div>` : ''}
  </div>
</div>
<div class="parties">
  <div class="box"><h2>Cliente</h2><strong>${esc(o.clientName || '—')}</strong></div>
  <div class="box"><h2>Situación</h2>${esc(o.statusLabel)}${o.expectedDelivery ? `<br>Entrega prevista: ${esc(o.expectedDelivery)}` : ''}${o.supplierName ? `<br>Proveedor: ${esc(o.supplierName)}` : ''}</div>
</div>
<table class="lines">
  <thead><tr><th>Título</th><th class="num">Cant.</th><th class="num">Estado</th>${o.hasPrices ? '<th class="num">Precio</th><th class="num">Importe</th>' : ''}</tr></thead>
  <tbody>${rows}</tbody>
</table>
${o.hasPrices ? `<div class="totals"><table>
  <tr class="grand"><td>TOTAL PAGADO</td><td class="num">${money(o.total)}</td></tr>
</table></div>` : ''}
<div class="foot">${o.hasPrices
        ? `Pedido ya abonado en el ticket ${esc(o.ticketNumber)}. Conserve este resguardo para recogerlo.`
        : 'Conserve este resguardo para recoger el pedido.'}<br>Le avisaremos en cuanto esté disponible en la librería.</div>
</body></html>`;
};

/**
 * La factura para el cuerpo de un correo: el mismo HTML que se imprime, sin la
 * indentacion ni los saltos de linea. /send_mail recibe el contenido por query
 * string, y asi ocupa bastante menos que el original.
 */
export const renderInvoiceEmailHtml = (inv: PrintInvoice): string =>
    renderInvoiceHtml(inv, 'email')
        // En papel los margenes los pone @page; en un correo no hay pagina, y la
        // factura quedaria pegada a los bordes. Se envuelve en un contenedor con
        // margenes laterales (en un div y no en el body, que varios clientes de
        // correo ignoran).
        .replace('</style></head><body>', '</style></head><body bgcolor="#e5e7eb" style="margin:0;background:#e5e7eb">'
            + '<table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation" bgcolor="#e5e7eb" style="background:#e5e7eb"><tr><td align="center" style="padding:24px 12px">'
            + '<table width="794" cellpadding="0" cellspacing="0" border="0" role="presentation" bgcolor="#ffffff" style="width:100%;max-width:794px;background:#ffffff;box-shadow:0 2px 12px rgba(0,0,0,.18)"><tr><td style="padding:56px;background:#ffffff">')
        .replace('</body>', '</td></tr></table></td></tr></table></body>')
        .replace(/\/\*[\s\S]*?\*\//g, '')
        .replace(/\s*\n\s*/g, ' ')
        .replace(/ {2,}/g, ' ')
        // La hoja de estilos sin espacios sobrantes: cada byte cuenta en la URL.
        .replace(/<style>([\s\S]*?)<\/style>/, (_m, css: string) =>
            `<style>${css.replace(/\s*([{};:,])\s*/g, '$1').replace(/;}/g, '}')}</style>`)
        .replace(/<title>[\s\S]*?<\/title>/, '')
        .trim();
